// Compact QC metric strip (peak / noise / length / misread). Friendly tone:
// pass = no badge, warn = amber chip, fail = red chip. The friendly text is
// canonicalized into @ohear/shared copy/qc — used here.

import { Badge, Tooltip } from '@ohear/ui'
import type { TakeQcMetrics } from '../../lib/api/producer-types'

export interface QcMetricsProps {
  metrics: TakeQcMetrics
}

export function QcMetrics({ metrics }: QcMetricsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      <Tooltip content={`Peak: ${metrics.peak_dbfs.toFixed(1)} dBFS`}>
        <span>
          <Badge variant={severityForPeak(metrics.peak_dbfs)}>
            peak {metrics.peak_dbfs.toFixed(1)}
          </Badge>
        </span>
      </Tooltip>
      <Tooltip content={`Noise floor: ${metrics.noise_floor_dbfs.toFixed(1)} dBFS`}>
        <span>
          <Badge variant={severityForNoise(metrics.noise_floor_dbfs)}>
            noise {metrics.noise_floor_dbfs.toFixed(1)}
          </Badge>
        </span>
      </Tooltip>
      <Tooltip
        content={`Length: ${metrics.duration_sec.toFixed(2)}s vs expected ${metrics.expected_duration_sec.toFixed(2)}s`}
      >
        <span>
          <Badge variant={severityForLength(metrics.duration_sec, metrics.expected_duration_sec)}>
            {metrics.duration_sec.toFixed(2)}s
          </Badge>
        </span>
      </Tooltip>
      {metrics.misread_score != null ? (
        <Tooltip content={`Misread score: ${(metrics.misread_score * 100).toFixed(0)}%`}>
          <span>
            <Badge variant={severityForMisread(metrics.misread_score)}>
              read {(metrics.misread_score * 100).toFixed(0)}%
            </Badge>
          </span>
        </Tooltip>
      ) : null}
    </div>
  )
}

function severityForPeak(peak: number): 'green' | 'amber' | 'red' {
  if (peak > -1) return 'red'
  if (peak > -3) return 'amber'
  return 'green'
}

function severityForNoise(noise: number): 'green' | 'amber' | 'red' {
  if (noise > -45) return 'red'
  if (noise > -55) return 'amber'
  return 'green'
}

function severityForLength(actual: number, expected: number): 'green' | 'amber' | 'red' {
  if (expected <= 0) return 'green'
  const ratio = Math.abs(actual - expected) / expected
  if (ratio > 0.4) return 'red'
  if (ratio > 0.2) return 'amber'
  return 'green'
}

function severityForMisread(score: number): 'green' | 'amber' | 'red' {
  if (score > 0.4) return 'red'
  if (score > 0.2) return 'amber'
  return 'green'
}
